import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';

import { ApiConversationService } from '../../../../api/services/agents/api-conversations.service';

import { SourceContentDto } from '../../../../api/dtos/conversation.dto';

export interface SourceViewerData {
  sources: {
    index: number;
    _id: string;
    name: string;
    preview: string;
    fullPreview: string;
    folder?: string[];
    fileLink?: string;
    embeddingContent?: string;
    embeddingID?: string;
    uiExternalRefsToDisplay?: string[];
    chunks?: string[]; // Array of text chunks for this source
    isLoaded?: boolean; // Track if full content has been loaded
    referencedEmbeddingIds?: string[]; // IDs of embeddings referenced in conversation (for filtering after lazy load)
  }[];
  activeIndex?: number;
}

@Component({
    selector: 'app-source-viewer-sidebar',
    templateUrl: './source-viewer-sidebar.component.html',
    styleUrls: ['./source-viewer-sidebar.component.scss'],
    standalone: true,
    imports: [MatIcon, MatIconButton, MatFormField, MatLabel, MatSelect, MatOption, MatProgressSpinner]
})
export class SourceViewerSidebarComponent implements OnChanges, AfterViewChecked, OnDestroy {
  private _apiService = inject(ApiConversationService);
  private _subscription?: Subscription;

  @Input() data: SourceViewerData = { sources: [] };
  @Input() visible = false;
  @Input() conversationId?: string; // For lazy loading source content
  @Output() closeSidebar = new EventEmitter<void>();

  @ViewChild('sourceTextContainer', { static: false }) sourceTextContainer?: ElementRef;

  selectedSourceIndex = 0;
  selectedChunkIndex = 0;
  totalSources = 0;
  isLoadingContent = false;

  // Badge pagination for large numbers of embeddings
  currentBadgePage = 0;
  badgesPerPage = 20;

  // Flag to trigger scroll after content update
  private shouldScrollToHighlight = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && this.data) {
      this.totalSources = this.data.sources.length;

      // Set the active source if provided
      if (
        this.data.activeIndex !== undefined &&
        this.data.activeIndex >= 0 &&
        this.data.activeIndex < this.data.sources.length
      ) {
        this.selectedSourceIndex = this.data.activeIndex;
      }

      // Reset chunk selection when source changes
      this.selectedChunkIndex = 0;
    }

    // When sidebar becomes visible, load content if needed
    if (changes.visible && this.visible) {
      this.loadSourceContentIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  // Reset chunk selection when source changes and load content
  onSourceSelectionChange(): void {
    this.selectedChunkIndex = 0;
    this.currentBadgePage = 0; // Reset to first page when source changes
    this.loadSourceContentIfNeeded();
  }

  /**
   * Load full source content on-demand if not already loaded.
   * This fetches the content from the new endpoint when viewing in results.
   */
  private loadSourceContentIfNeeded(): void {
    const currentSource = this.data.sources[this.selectedSourceIndex];
    if (!currentSource || !this.conversationId) return;

    // Skip if already loaded
    if (currentSource.isLoaded) return;

    this.isLoadingContent = true;
    this._subscription?.unsubscribe();

    this._subscription = this._apiService
      .getSourceContent(this.conversationId, currentSource._id)
      .subscribe({
        next: (response: SourceContentDto) => {
          // Update the source with loaded content
          currentSource.fullPreview = response.fullContent || '';
          
          // Filter embeddings to only those referenced in conversation (if we have the IDs)
          if (currentSource.referencedEmbeddingIds && currentSource.referencedEmbeddingIds.length > 0) {
            const referencedIds = new Set(currentSource.referencedEmbeddingIds);
            const filteredEmbeddings = response.embeddings.filter(e => referencedIds.has(e.embeddingId));
            
            // Use filtered embeddings if any match, otherwise fall back to all
            if (filteredEmbeddings.length > 0) {
              currentSource.chunks = filteredEmbeddings.map(e => e.content);
            } else {
              // Fallback: show all embeddings if none match (shouldn't happen normally)
              currentSource.chunks = response.embeddings.map(e => e.content);
            }
          } else {
            // No referenced IDs - show all embeddings
            currentSource.chunks = response.embeddings.map(e => e.content);
          }
          
          currentSource.isLoaded = true;
          this.isLoadingContent = false;
          
          // Trigger scroll to first highlight after content loads
          if (currentSource.chunks && currentSource.chunks.length > 0) {
            this.shouldScrollToHighlight = true;
          }
        },
        error: (err) => {
          console.error('Error loading source content:', err);
          currentSource.fullPreview = 'Error loading content';
          this.isLoadingContent = false;
        }
      });
  }

  // Get chunks for current source
  get currentSourceChunks(): string[] {
    const currentSource = this.data.sources[this.selectedSourceIndex];
    return currentSource?.chunks || [];
  }

  // Check if current source has chunks
  get hasChunks(): boolean {
    return this.currentSourceChunks.length > 0;
  }

  // Get currently selected chunk text
  get selectedChunkText(): string {
    const chunks = this.currentSourceChunks;
    return chunks[this.selectedChunkIndex] || '';
  }

  closeDialog(): void {
    this.closeSidebar.emit();
  }

  truncateSourceName(name: string): string {
    if (!name) return 'Untitled Source';
    return name.length > 50 ? name.substring(0, 47) + '...' : name;
  }

  // Get highlighted content with selected embedding chunk highlighted
  getHighlightedContent(): string {
    const currentSource = this.data.sources[this.selectedSourceIndex];
    if (!currentSource || !this.hasChunks) {
      return `<p>${currentSource?.fullPreview || ''}</p>`;
    }

    const selectedChunk = this.selectedChunkText;
    const content = currentSource.fullPreview || '';

    if (selectedChunk && content.includes(selectedChunk)) {
      // Exact match - highlight the selected chunk within the full content
      const highlightedContent = content.replace(
        selectedChunk,
        `<mark class="embedding-highlight">${selectedChunk}</mark>`
      );
      return `<p>${highlightedContent}</p>`;
    }

    // Try normalized matching (remove extra whitespace)
    if (selectedChunk) {
      const normalizedChunk = this.normalizeWhitespace(selectedChunk);
      const normalizedContent = this.normalizeWhitespace(content);
      
      if (normalizedContent.includes(normalizedChunk)) {
        // Find approximate position and highlight
        // Try to highlight in original content using approximate position
        const words = normalizedChunk.split(/\s+/).slice(0, 5).join('\\s+');
        const regex = new RegExp(`(${words}.{0,500}?)(?=\\s|$)`, 'i');
        const match = content.match(regex);
        if (match) {
          const highlightedContent = content.replace(
            match[0],
            `<mark class="embedding-highlight">${match[0]}</mark>`
          );
          return `<p>${highlightedContent}</p>`;
        }
      }
    }

    return `<p>${content}</p>`;
  }

  // Normalize whitespace for fuzzy matching
  private normalizeWhitespace(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
  }

  // Check if chunk can be highlighted in content
  get canHighlightChunk(): boolean {
    const selectedChunk = this.selectedChunkText;
    const content = this.data.sources[this.selectedSourceIndex]?.fullPreview || '';
    if (!selectedChunk || !content) return false;
    
    return content.includes(selectedChunk) || 
           this.normalizeWhitespace(content).includes(this.normalizeWhitespace(selectedChunk));
  }

  // Badge pagination methods
  get totalBadgePages(): number {
    return Math.ceil(this.currentSourceChunks.length / this.badgesPerPage);
  }

  getVisibleBadges(): string[] {
    const startIndex = this.currentBadgePage * this.badgesPerPage;
    const endIndex = startIndex + this.badgesPerPage;
    return this.currentSourceChunks.slice(startIndex, endIndex);
  }

  getBadgeGlobalIndex(localIndex: number): number {
    return this.currentBadgePage * this.badgesPerPage + localIndex;
  }

  nextBadgePage(): void {
    if (this.currentBadgePage < this.totalBadgePages - 1) {
      this.currentBadgePage++;
    }
  }

  previousBadgePage(): void {
    if (this.currentBadgePage > 0) {
      this.currentBadgePage--;
    }
  }

  // New method to select embedding and scroll to it
  selectEmbeddingAndScroll(chunkIndex: number): void {
    this.selectedChunkIndex = chunkIndex;
    this.shouldScrollToHighlight = true;
  }

  // Lifecycle method to handle scrolling after view updates
  ngAfterViewChecked(): void {
    if (this.shouldScrollToHighlight) {
      this.scrollToHighlightedText();
      this.shouldScrollToHighlight = false;
    }
  }

  // Method to instantly scroll to the highlighted text
  private scrollToHighlightedText(): void {
    if (!this.sourceTextContainer) return;

    // Use setTimeout to ensure the DOM has been updated with the new highlight
    setTimeout(() => {
      const container = this.sourceTextContainer?.nativeElement;
      if (!container) return;

      const highlightElement = container.querySelector('mark.embedding-highlight');
      if (highlightElement) {
        highlightElement.scrollIntoView({
          behavior: 'auto', // No animation
          block: 'center', // Center the highlighted text in view
          inline: 'nearest', // Don't scroll horizontally unless needed
        });
      }
    }, 0);
  }
}
